// assets/stz-ai-acknowledgement.js
// AI acknowledgement gate for personalized / digital products. Sibling to birth-data-gate.js and
// age-fit.js: intercepts the add-to-cart submit in the capture phase until the box is ticked, shows
// an inline error (never an alert), dims the express block, and on tick enables the two hidden
// line-item properties (_AI Acknowledged = ISO timestamp, _AI Disclosure Version) the order keeps.
// Markup: snippets/stz-ai-acknowledgement.liquid.

class StzAiAcknowledgement extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.box = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-stz-ai-ack-box]'));
    this.error = /** @type {HTMLElement | null} */ (this.querySelector('[data-stz-ai-ack-error]'));
    this.at = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-stz-ai-ack-at]'));
    this.version = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-stz-ai-ack-version]'));
    if (!this.box) return;

    const formId = this.dataset.formId;
    this.form =
      (formId && document.getElementById(formId)) ||
      this.closest('form') ||
      this.closest('product-form-component')?.querySelector('form') ||
      null;

    // component.js delegates `on:submit="/handleSubmit"` on `document` in the CAPTURE phase, which
    // runs before any listener on the form itself. Only a window-capture listener runs earlier, so
    // that is where the gate lives; it filters to this form.
    this.onSubmitBound = (e) => { if (e.target === this.form) this.onSubmit(e); };
    window.addEventListener('submit', this.onSubmitBound, true);
    this.box.addEventListener('change', () => this.apply());
    this.apply();
  }

  disconnectedCallback() {
    if (this.onSubmitBound) window.removeEventListener('submit', this.onSubmitBound, true);
  }

  get acknowledged() {
    return Boolean(this.box && this.box.checked);
  }

  /** Mirror the tick into the hidden properties and the buttons' gated state. */
  apply() {
    const on = this.acknowledged;
    if (this.at) {
      this.at.disabled = !on;
      this.at.value = on ? new Date().toISOString() : '';
    }
    if (this.version) this.version.disabled = !on;
    if (on && this.error) this.error.hidden = true;
    this.removeAttribute('data-invalid');
    for (const button of this.buttons()) {
      if (on) button.removeAttribute('aria-disabled');
      else button.setAttribute('aria-disabled', 'true');
    }
    for (const block of this.expressBlocks()) block.classList.toggle('is-gated', !on);
  }

  /** @returns {HTMLElement[]} */
  buttons() {
    if (!this.form) return [];
    return Array.from(this.form.querySelectorAll('button[type="submit"], .add-to-cart-button'));
  }

  /** @returns {HTMLElement[]} */
  expressBlocks() {
    const scope = this.closest('product-form-component') || this.form?.parentElement || document;
    return Array.from(scope.querySelectorAll('.accelerated-checkout-block, .additional-checkout-buttons'));
  }

  /** @param {SubmitEvent} e */
  onSubmit(e) {
    if (this.acknowledged) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    this.setAttribute('data-invalid', 'true');
    if (this.error) this.error.hidden = false;
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.box?.focus();
  }
}

if (!customElements.get('stz-ai-acknowledgement')) {
  customElements.define('stz-ai-acknowledgement', StzAiAcknowledgement);
}
